import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import useKeyboard from "../hooks/useKeyboard";
import MediaComponent from "./MediaComponent";
import "./SlideViewer.css";

const SlideViewer = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clientData, setClientData] = useState(null);

  // Load client slides from auth context
  useEffect(() => {
    const loadClientSlides = () => {
      try {
        setLoading(true);

        if (!user || !user.assigned_clients || !id) {
          setError("User data or client ID not available");
          setLoading(false);
          return;
        }

        // Find the specific client by ID
        const client = user.assigned_clients.find(
          (client) => client._id === id
        );

        if (!client) {
          setError("Client not found");
          setLoading(false);
          return;
        }

        if (!client.client_slides || client.client_slides.length === 0) {
          setError("No slides available for this client");
          setLoading(false);
          return;
        }

        // Map client slides to the format expected by the component
        const formattedSlides = client.client_slides.map((slide, index) => ({
          id: slide._id || index + 1,
          title: slide.description || slide.title || `Slide ${index + 1}`,
          content:
            slide.description || `${client.client_name} - Slide ${index + 1}`,
          image: slide.location,
          slideNumber: index + 1,
        }));

        setSlides(formattedSlides);
        setClientData(client);
        setError(null);
      } catch (err) {
        console.error("Error loading client slides:", err);
        setError("Failed to load client slides");
      } finally {
        setLoading(false);
      }
    };

    loadClientSlides();
  }, [user, id]);

  const handleSlideChange = (slideIndex) => {
    setCurrentSlide(slideIndex);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToFirstSlide = () => {
    setCurrentSlide(0);
  };

  const goToLastSlide = () => {
    setCurrentSlide(slides.length - 1);
  };

  // Keyboard navigation
  useKeyboard({
    onNext: nextSlide,
    onPrevious: prevSlide,
    onFirst: goToFirstSlide,
    onLast: goToLastSlide,
  });

  if (loading) {
    return <div className="loading">Loading client slides...</div>;
  }

  if (error) {
    return (
      <div className="loading">
        <div>{error}</div>
        <div>Please go back to the dashboard and select a valid client.</div>
      </div>
    );
  }

  if (slides.length === 0) {
    return (
      <div className="loading">No slides available for this client...</div>
    );
  }

  return (
    <div className="slide-viewer">
      {/* Sidebar Navigation */}
      <div className="sidebar">
        <h3>Slides</h3>
        <div className="slide-list">
          {slides.map((slide, index) => (
            <div
              key={slide.id}
              className={`slide-item ${index === currentSlide ? "active" : ""}`}
              onClick={() => handleSlideChange(index)}>
              <div className="slide-number">{index + 1}</div>
              <div className="slide-title">{slide.title}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="main-content">
        <div className="slide-header">
          <h1>
            {clientData ? `${clientData.client_name}` : "Client Presentation"}
          </h1>
          <div className="slide-counter">
            {currentSlide + 1} of {slides.length}
          </div>
        </div>

        <div className="slide-display">
          <div className="slide-content">
            <MediaComponent
              key={currentSlide}
              src={slides[currentSlide].image}
              alt={`${clientData?.client_name || "Client"} - Slide ${
                currentSlide + 1
              }`}
              className="slide-media"
              autoPlay={true}
              controls={false}
              muted={true}
              loop={false}
            />
          </div>
        </div>

        {/* Navigation Controls */}
        <div className="slide-controls">
          <button
            onClick={prevSlide}
            disabled={currentSlide === 0}
            className="control-btn prev-btn">
            ← Previous
          </button>

          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{
                width: `${((currentSlide + 1) / slides.length) * 100}%`,
              }}></div>
          </div>

          <button
            onClick={nextSlide}
            disabled={currentSlide === slides.length - 1}
            className="control-btn next-btn">
            Next →
          </button>
        </div>
      </div>
    </div>
  );
};

export default SlideViewer;
